<template>
	<v-menu
		ref="metadataPreview"
		v-model="dialog"
		content-class="metadata-preview"
		:open-on-hover="true"
		:persistent="true"
		:close-on-content-click="false"
		:min-width="minWidth"
		:min-height="minHeight"
		transition="false"
	>
		<template #activator="{ props }">
			<div>
				<slot v-bind="props" name="activator"></slot>
			</div>
		</template>
		<div class="menu-container">
			<div class="triangle" :style="triangleStyle"></div>
			<v-card
				class="pb-5"
				max-width="600"
				:style="{
					border: `1px solid ${borderColor}`,
					borderRadius: '4px',
				}"
			>
				<v-card-text>
					<v-row>
						<v-col
							class="py-1 d-flex align-center justify-start font-weight-bold text-subtitle-1"
						>
							{{ tagGroupName }}
						</v-col>
					</v-row>
					<v-row class="font-weight-bold border-bottom border-color">
						<v-col v-if="hasAxisData()" cols="3">
							{{ headerRow.axisLabel }}
						</v-col>
						<v-col :cols="hasAxisData() ? 5 : 6">
							{{ headerRow.valueLabel }}
						</v-col>
						<v-col :cols="hasAxisData() ? 4 : 6">
							{{ headerRow.tagNameLabel }}
						</v-col>
					</v-row>
					<v-row
						v-for="(row, index) in rowData"
						:key="row.tagName"
						:class="{
							'border-bottom': index !== rowData.length - 1,
						}"
					>
						<v-col
							v-if="hasAxisData()"
							class="py-1 d-flex align-center"
							cols="3"
						>
							<div v-if="row.axis">
								<v-icon size="x-small"> mdi-chart-line </v-icon>
								{{ row.axis }}
							</div>
							<div
								v-else-if="row.fromBroadcast && row.datasetUuid"
								title="Per-branch default tag"
							>
								<v-icon size="x-small"> mdi-chart-line-variant </v-icon>
							</div>
							<div v-else-if="row.fromBroadcast" title="Graph-wide default tag">
								<v-icon size="x-small"> mdi-chart-line </v-icon>
							</div>
						</v-col>
						<v-col
							class="py-1 d-flex align-center"
							:cols="hasAxisData() ? 5 : 6"
						>
							{{ row.value }}
						</v-col>
						<v-col
							class="py-1 d-flex align-center"
							:cols="hasAxisData() ? 4 : 6"
						>
							{{ row.tagName }}
						</v-col>
					</v-row>
					<div v-if="!isAbbreviation">
						<v-divider class="my-8 mx-n4 opacity-100" :color="borderColor" />
						<div v-if="compositeKeyData.length > 0" class="position-relative">
							<v-row>
								<v-col
									v-for="item in compositeKeyData"
									:key="item.value"
									:cols="12 / compositeKeyData.length"
									class="py-1 d-flex align-center text-caption font-weight-bold"
								>
									{{ item.label }}
								</v-col>
							</v-row>
							<v-row>
								<v-col
									v-for="item in compositeKeyData"
									:key="item.value"
									:cols="12 / compositeKeyData.length"
									class="py-1 text-caption"
								>
									{{ item.value }}
								</v-col>
							</v-row>
						</div>
						<div v-else>
							<v-row>
								<v-col class="py-1 d-flex align-center">
									<v-icon class="mr-3" color="red" size="x-large">
										mdi-alert-circle
									</v-icon>
									Missing study branch
								</v-col>
							</v-row>
						</div>
					</div>
				</v-card-text>
			</v-card>
		</div>
	</v-menu>
</template>

<script>
import { useTheme } from "vuetify";
import createCompositeKeyUtils from "@/compositeKeyUtils";

export default {
	name: "VMetadataPreview",

	props: {
		bounds: { type: Object, default: null },
	},

	setup() {
		const theme = useTheme();
		return { theme };
	},

	data() {
		return {
			compositeKeyData: [],
			data: {},
			dialog: false,
			headerRow: {
				axisLabel: "Axis",
				tagNameLabel: "Tag",
				valueLabel: "Value",
			},
			isAbbreviation: false,
			position: { x: 0, y: 0 },
			offset: 12,
			minWidth: 300,
			minHeight: 100,
			width: 300,
			height: 100,
			rowData: [],
			tagGroupName: "",
			triangleStyle: {
				position: "absolute",
				width: "0",
				height: "0",
				border: "8px solid transparent",
			},
		};
	},

	computed: {
		borderColor() {
			return this.theme.current.value.colors.secondary;
		},
	},

	methods: {
		async open(args, tags) {
			this.compositeKeyUtils = createCompositeKeyUtils(tags);
			this.position = args.position;
			this.data = args.data;

			if (!args.data) return;

			this.rowData = Object.keys(this.data)
				.filter((key) => !isNaN(key) && !this.data[key].hidden)
				.map((key) => {
					return {
						tagName: this.data[key].tag.name,
						value: this.data[key].value,
						axis: this.data[key].axis,
						fromBroadcast: this.data[key].fromBroadcast,
						datasetUuid: this.data[key].datasetUuid,
					};
				});

			this.tagGroupName = this.data.tagGroup.name;
			this.compositeKeyData =
				this.compositeKeyUtils.parseCompositeKeyWithLabels(
					this.data.compositeKey,
				);

			this.isAbbreviation =
				this.tagGroupName === "Definitions and abbreviations";
			this.dialog = true;

			await this.$nextTick();
			const element = document.getElementsByClassName("metadata-preview")[0];

			const arrowColor = this.theme.current.value.colors.secondary;
			if (element) {
				element.style.pointerEvents = "none";
				const y = this.position.y + this.bounds.top;
				const x = this.position.x + this.bounds.left;

				const viewportBounds = element.parentElement.getBoundingClientRect();
				const viewportMaxY = viewportBounds.height;
				const viewportMaxX = viewportBounds.width;
				const menuWidth = element.getBoundingClientRect().width;
				const menuHeight = element.getBoundingClientRect().height;
				this.height = menuHeight;
				this.width = menuWidth;

				const direction = this.determineDirection(x, y);

				// Update triangle style based on direction
				switch (direction) {
					case "north":
						this.triangleStyle = {
							...this.triangleStyle,
							bottom: "-8px",
							left: "50%",
							transform: "translateX(-50%)",
							borderTopColor: arrowColor,
							borderLeft: "8px solid transparent",
							borderRight: "8px solid transparent",
							borderBottom: "none",
						};
						element.style.bottom = `${viewportMaxY - y + this.offset}px`;
						element.style.left = `${x - menuWidth / 2}px`;
						break;
					case "east":
						this.triangleStyle = {
							...this.triangleStyle,
							left: "-8px",
							top: "50%",
							transform: "translateY(-50%)",
							borderRightColor: arrowColor,
							borderTop: "8px solid transparent",
							borderBottom: "8px solid transparent",
							borderLeft: "none",
						};
						element.style.top = `${y - menuHeight / 2}px`;
						element.style.left = `${x + this.offset}px`;
						break;
					case "south":
						this.triangleStyle = {
							...this.triangleStyle,
							top: "-8px",
							left: "50%",
							transform: "translateX(-50%)",
							borderBottomColor: arrowColor,
							borderLeft: "8px solid transparent",
							borderRight: "8px solid transparent",
							borderTop: "none",
						};
						element.style.top = `${y + this.offset}px`;
						element.style.left = `${x - menuWidth / 2}px`;
						break;
					case "west":
						this.triangleStyle = {
							...this.triangleStyle,
							right: "-8px",
							top: "50%",
							transform: "translateY(-50%)",
							borderLeftColor: arrowColor,
							borderTop: "8px solid transparent",
							borderBottom: "8px solid transparent",
							borderRight: "none",
						};
						element.style.top = `${y - menuHeight / 2}px`;
						element.style.right = `${viewportMaxX - x + this.offset}px`;
						break;
				}
			}
		},
		close() {
			this.dialog = false;
			const element = document.getElementsByClassName("metadata-preview")[0];
			if (element) {
				element.style.pointerEvents = "";
			}
			this.triangleStyle = {
				position: "absolute",
				width: "0",
				height: "0",
				border: "8px solid transparent",
			};
		},
		determineDirection(x, y) {
			let direction = "south";
			if (this.bounds.bottom - y < this.height) {
				direction = "north";
			}
			if (x - this.bounds.left < this.width / 2) {
				direction = "east";
			}
			if (this.bounds.right - x < this.width / 2) {
				direction = "west";
			}

			return direction;
		},
		hasAxisData() {
			return this.rowData.some((row) => !!row.axis || !!row.fromBroadcast);
		},
	},
};
</script>

<style scoped lang="scss">
.menu-container {
	position: relative;
}

.triangle {
	position: absolute;
	width: 0;
	height: 0;
	border: 8px solid transparent;
}

.border-bottom {
	position: relative;
}

.border-bottom::after {
	content: "";
	position: absolute;
	bottom: 0;
	left: 8px;
	right: 8px;
	border-bottom: 1px solid var(--border-default);
}
</style>
